import { IonCard, IonCardContent, IonCol, IonGrid, IonImg, IonRow, IonText } from '@ionic/react';
import { useState } from 'react';
import { useHistory } from 'react-router';

import { ActionSheetReportCard } from '@/components/Actionsheets';
import { Icon } from '@/components/Icon';
import { IconSize } from '@/components/Icon/types';
import { CustomLoading } from '@/components/Loading/CustomLoading';
import { Tag } from '@/components/Tag/Tag';
import { Toast } from '@/components/Toasts';

import { getTitleAndDescriptionFromInfo } from '@/utils/getTitleAndDescriptionFromReportData';
import { mergeClassNames } from '@/utils/mergeClassNames';

import { useReportContext } from '@/contexts/reportContext';

import Running from '@/assets/icons/Running.png';
import slsquatter from '@/assets/icons/SingleLegSquat.png';
import squatter from '@/assets/icons/Squat.png';
import Walking from '@/assets/icons/Walking.png';
import ReportCardPattern from '@/assets/report-card-pattern.svg';

import { ReportInfo, ReportStatus, ReportType } from '@/models';

import styles from './SessionCard.module.css';

interface ReportCardProps {
  id: string;
  name: string;
  date: Date;
  reportType: ReportType;
  reportInfo: ReportInfo;
  tags?: string[];
}

export const SessionReportCard = ({ id, name, date, reportType, reportInfo, tags }: ReportCardProps) => {
  const history = useHistory();
  const { title, description } = getTitleAndDescriptionFromInfo(name, date);
  const backTarget = `/clients/${id}`;
  const pending = reportInfo.status === ReportStatus.PENDING;
  const error =
    reportInfo.status === ReportStatus.ERROR ||
    reportInfo.reportType === ReportType.ERROR ||
    reportInfo.reportType === ReportType.KNEEL ||
    reportInfo.reportType === ReportType.KNEER;

  const { doDelete } = useReportContext();
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showErrorDoDelete, setShowErrorDoDelete] = useState(false);
  const [PDFError, setPDFError] = useState(false);

  const imageMap = new Map([
    [ReportType.WALK, Walking],
    [ReportType.RUN, Running],
    [ReportType.SQUAT, squatter],
    [ReportType.SLSQUAT, slsquatter],
  ]);

  const activityNamesMap = new Map([
    [ReportType.WALK, 'Walk'],
    [ReportType.RUN, 'Run'],
    [ReportType.SQUAT, 'Squat'],
    [ReportType.SLSQUAT, 'Single Leg Squat'],
  ]);

  const routeSegmentMap = new Map([
    [ReportType.WALK, 'Walk'],
    [ReportType.RUN, 'Run'],
    [ReportType.SQUAT, 'Squat'],
    [ReportType.SLSQUAT, 'SingleLegSquat'],
  ]);

  const handleClick = () => {
    if (routeSegmentMap.has(reportType)) {
      history.push(`/reportsV2/${routeSegmentMap.get(reportType)}/${id}`, {
        title,
        description,
        backTarget,
      });
    }
  };

  return (
    <>
      <ActionSheetReportCard
        doDelete={doDelete}
        isError={error}
        isOpen={showActionSheet}
        onClose={() => setShowActionSheet(false)}
        reportInfo={reportInfo}
        showDeleteError={setShowErrorDoDelete}
        showPDFError={setShowErrorDoDelete}
        // onGeneratePDF={handleGeneratePDF}
      />
      <IonCard
        className={mergeClassNames(styles.card, error ? styles.error : '')}
        onClick={!error && !pending ? handleClick : undefined}>
        {pending ? (
          <div className={styles.pending}>
            <CustomLoading message={'Creating report...'} size='small' />
          </div>
        ) : (
          <>
            <div className={mergeClassNames(styles.color, error ? styles.error : '')}>
              {error ? (
                <Icon color='#E7364B' name='error' size={IconSize.extraLarge} />
              ) : (
                <IonImg className={styles.icon} src={imageMap.get(reportType) ?? Walking} />
              )}
              <IonImg className={styles.pattern} src={ReportCardPattern} />
            </div>

            <IonCardContent className={styles.content}>
              <IonGrid className='ion-no-padding'>
                <IonRow className='ion-align-items-center' style={{ width: '100%' }}>
                  {error ? (
                    <IonCol className='ion-no-padding' size='9'>
                      <IonRow style={{ marginBottom: '16px' }}>
                        <IonText className={styles.name}>{reportInfo.reportName}</IonText>
                      </IonRow>
                      <IonRow>
                        <Tag size='md' variant='error'>
                          {reportInfo.status === ReportStatus.ERROR &&
                            `Sorry, we couldn't process your data. Please contact support.`}
                          {reportInfo.reportType === ReportType.ERROR &&
                            `Sorry, we couldn't process your data. Please contact support.`}
                          {reportInfo.reportType === ReportType.KNEEL &&
                            `Left Thigh and Calf Sensors seem to be swapped.`}
                          {reportInfo.reportType === ReportType.KNEER &&
                            `Right Thigh and Calf Sensors seem to be swapped.`}
                        </Tag>
                      </IonRow>
                    </IonCol>
                  ) : (
                    <>
                      <IonCol className='ion-no-padding' size='9'>
                        <IonRow style={{ marginBottom: '16px' }}>
                          <IonText className={styles.name}>{activityNamesMap.get(reportType)}</IonText>
                        </IonRow>
                        <IonRow>
                          {tags ? (
                            <>
                              {tags.map((tag, index) => (
                                <Tag key={index} size='sm'>
                                  {' '}
                                  {tag}{' '}
                                </Tag>
                              ))}
                            </>
                          ) : null}
                        </IonRow>
                      </IonCol>
                    </>
                  )}
                  <IonCol className='ion-no-padding ion-text-center ' size='2'>
                    <IonText class={styles.date}>
                      {date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </IonText>
                  </IonCol>
                  <IonCol
                    className='ion-no-padding '
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowActionSheet(true);
                    }}
                    size='1'>
                    <Icon className={styles.iconMenu} color='#829696' name='menu' size={IconSize.small} />
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonCardContent>
          </>
        )}
      </IonCard>

      <Toast
        header='Error deleting file'
        isOpen={showErrorDoDelete}
        message={`No report ID for the given report ${reportInfo.filename}.`}
        onDidDismiss={() => setShowErrorDoDelete(false)}
        type='warning'
      />
      <Toast
        header='Error downloading file'
        isOpen={PDFError}
        message={`Can't download PDF for ${reportInfo.filename}, please contact support.`}
        onDidDismiss={() => setPDFError(false)}
        type='warning'
      />
    </>
  );
};
